/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import { If, Then, Else } from "react-if";
import { FlexContainer, Text, theme, ExtendedTooltip } from "@nordcloud/gnui";
import {
  getAttentionNeededDescription,
  getResourceStatusConfig,
  getSuccessDetailedStatusConfig,
  hasSuccessfulStatus,
} from "~/components/resourceGroup/utils";
import { StyledSVGIcon } from "~/components/Styled/StyledSVGIcon";
import { TooltipText } from "~/components/Styled/TooltipText";
import { NameWithLink } from "~/components/Tables/cells/NameWithLink";
import {
  FlexColumnSmall,
  MediumColumn,
  MediumIconColumn,
  SmallColumn,
} from "~/components/Tables/styles";
import { dateFormat } from "~/constants";
import { ROUTES } from "~/routing/routes";
import {
  getLogActionDescription,
  hasLogRelatedErrorCode,
} from "~/utils/logErrorCode";
import { PastActionForResourceWithResourceId } from "../../types/ResourceDetailsActionsQuery";
import { ResourceDetailsActionsTableActions } from "./ResourceDetailsActionsTableActions";

const columnHelper = createColumnHelper<PastActionForResourceWithResourceId>();

function getActionDescription({
  errorCode,
}: Pick<PastActionForResourceWithResourceId, "errorCode">) {
  return getLogActionDescription(errorCode);
}

export function columns() {
  return [
    columnHelper.accessor("status", {
      header: "Resource Status",
      cell: ({ row }) => {
        const { status, errorCode } = row.original;
        const isAttentionNeeded =
          hasSuccessfulStatus(status) && hasLogRelatedErrorCode(errorCode);

        const baseStatusConfig = getResourceStatusConfig(status);
        const statusConfig = isAttentionNeeded
          ? getSuccessDetailedStatusConfig(baseStatusConfig)
          : baseStatusConfig;
        const statusDescription = isAttentionNeeded
          ? getAttentionNeededDescription(statusConfig.description)
          : statusConfig.description;

        return (
          <FlexContainer justifyContent="center" alignItems="center">
            <ExtendedTooltip
              status={statusConfig.tooltipColor}
              caption={statusDescription}
              hideTimeout={0}
              position="start"
            >
              <StyledSVGIcon
                name={statusConfig.icon}
                color={statusConfig.color()}
                pathColors={statusConfig.pathColors}
              />
            </ExtendedTooltip>
          </FlexContainer>
        );
      },
      meta: MediumIconColumn,
    }),
    columnHelper.accessor("actionName", {
      id: "type",
      header: "Action Name/Type",
      cell: ({ row }) => {
        return (
          <FlexContainer direction="column" alignItems="start">
            <NameWithLink
              name={row.original.actionName}
              link={ROUTES.events.generateDetails(
                row.original.eventId ?? "",
                row.original.eventActionId ?? ""
              )}
            />
            <Text tag="div" size="sm" color={theme.color.text.text02}>
              {row.original.actionType}
            </Text>
          </FlexContainer>
        );
      },
      meta: FlexColumnSmall,
    }),
    columnHelper.accessor("resourceGroupName", {
      header: "Resource Group Name",
      cell: ({ row }) => {
        return (
          <If
            condition={
              row.original.resourceGroupName && row.original.resourceGroupId
            }
          >
            <Then>
              <NameWithLink
                name={row.original.resourceGroupName ?? ""}
                link={ROUTES.resourceGroups.generateDetails(
                  row.original.resourceGroupId ?? ""
                )}
              />
            </Then>
            <Else>
              <Text color={theme.color.text.text03}>No Resource Group</Text>
            </Else>
          </If>
        );
      },
      meta: MediumColumn,
    }),
    columnHelper.accessor("eventName", {
      header: "Event Name",
      cell: ({ row }) => {
        return (
          <NameWithLink
            name={row.original.eventName}
            link={ROUTES.events.generateDetails(row.original.eventId)}
          />
        );
      },
      meta: MediumColumn,
    }),
    columnHelper.accessor("endTime", {
      header: "End Time (local time)",
      cell: ({ row }) => {
        const endTimeTZ = dayjs(row.original.endTime)
          .tz(row.original.scheduleTimezone)
          .format(dateFormat.dayMonthYearHourMinute);

        return (
          <FlexContainer alignItems="center">
            <ExtendedTooltip
              caption={
                <>
                  <If condition={!!row.original.endTime}>
                    <Then>Plan Time: {endTimeTZ}</Then>
                    <Else>End Time will appear when the event is complete</Else>
                  </If>
                  <br />
                  Timezone: {row.original.scheduleTimezone}
                </>
              }
            >
              <TooltipText
                size="md"
                tag="span"
                mr={theme.spacing.spacing01}
                weight="regular"
                color={theme.color.text.text01}
              >
                <If condition={!!row.original.endTime}>
                  <Then>
                    {dayjs(row.original.endTime).format(
                      dateFormat.dayMonthYearHourMinute
                    )}
                  </Then>
                  <Else>Awaiting Completion</Else>
                </If>
              </TooltipText>
            </ExtendedTooltip>
          </FlexContainer>
        );
      },
      meta: SmallColumn,
    }),
    columnHelper.accessor("errorCode", {
      header: "Details",
      cell: ({ row }) => {
        const description = getActionDescription(row.original);

        if (!description) {
          return "-";
        }

        return (
          <FlexContainer
            alignItems="center"
            gap={theme.spacing.spacing02}
            css={{ minWidth: 0 }}
          >
            {description.icon ? (
              <StyledSVGIcon
                name={description.icon}
                color={description.iconColor}
              />
            ) : null}
            <div>{description.text}</div>
          </FlexContainer>
        );
      },
      meta: MediumColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "options",
      header: () => null,
      cell: ({ row }) => {
        return (
          <ResourceDetailsActionsTableActions
            planId={row.original.planId}
            resourceId={row.original.resourceId}
            eventId={row.original.eventId}
            eventActionId={row.original.eventActionId}
            outputUri={row.original.outputUri ?? ""}
            actionName={row.original.actionName}
            resourceExecutionStatus={row.original.status}
          />
        );
      },
      meta: MediumIconColumn,
    }),
  ] as never;
}
